import {workState,timerCommand} from './contributionApi';

import {
  Farm, FarmTract, FarmField, CRPContract, ContractPractice, ContractField,
  MapFeature, Document, OMTask, CorrespondenceMessage, RecordLink,
  Asset, Transaction, Contact, BaseEntity, ScoutingPhoto, ScoutingPhotoCategory, DocumentSource, ScoutingPhotoUploadRequest, Project, ProjectStatus, WorkLog, deriveWorkLogDurationMs
} from '../types';
import { db, migrateFromLocalStorage } from './db';
import { getAuthToken } from './auth';
import { SyncService } from './syncService';
import {
  EntityType,
  getRepositoryForEntity,
  getRepositoryKind,
} from './dataAccessBoundary';
import { ServerRepository } from './serverRepository';
import { ScoutingClient, ScoutingPhotoFilters } from './scoutingClient';
import { getEffectiveFilterCategoryIds, getEffectivePhotoCategoryIds } from './scoutingPhotoCategories';

const getEnv = (key: string): string => {
    if (typeof process !== 'undefined' && process.env && typeof process.env[key] === 'string') {
        return process.env[key] as string;
    }
    if (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env[key]) {
        return (import.meta as any).env[key];
    }
    return '';
};

const shouldSeedDefaultFarm = () => {
    const explicitFlag = getEnv('VITE_ENABLE_DEFAULT_SEED');
    // If an explicit flag is provided, honor it to let real backends opt out.
    if (explicitFlag) {
        return explicitFlag.toLowerCase() === 'true';
    }
    // Demo data is opt-in; an empty local cache must not enqueue a demo farm.
    return false;
};

const DEFAULT_FARM_BOUNDARY = JSON.stringify([
    [
        [41.2695, -89.4945],
        [41.2695, -89.4903],
        [41.2655, -89.4903],
        [41.2655, -89.4945],
        [41.2695, -89.4945]
    ]
]);

// --- Initialization ---
// Perform migration check once on module load
migrateFromLocalStorage().catch(console.error);


const DEFAULT_SCOUTING_CATEGORY_ID = 'field-captures';
const SCOUTING_MIGRATION_KEY = 'scouting_photo_migration_v1';

const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return Math.random().toString(36).slice(2);
};



const normalizeRecordLink = (link: RecordLink): RecordLink => {
  const fromType = link.from_record_type || link.entity_type;
  const fromId = link.from_record_id || link.entity_id;
  const toType = link.to_record_type || (link.document_id ? 'document' : link.photo_id ? 'scouting_photo' : link.message_id ? 'message' : undefined);
  const toId = link.to_record_id || link.document_id || link.photo_id || link.message_id;
  return {
    ...link,
    from_record_type: fromType as RecordLink['from_record_type'],
    from_record_id: fromId as string,
    to_record_type: toType as RecordLink['to_record_type'],
    to_record_id: toId as string,
    entity_type: link.entity_type || fromType,
    entity_id: link.entity_id || fromId,
    document_id: link.document_id || (toType === 'document' ? toId : undefined),
    photo_id: link.photo_id || (toType === 'scouting_photo' ? toId : undefined),
    message_id: link.message_id || (toType === 'message' ? toId : undefined),
  };
};

const linkMatchesRecord = (link: RecordLink, recordType: string, recordId: string): boolean => {
  const normalized = normalizeRecordLink(link);
  return (
    (normalized.from_record_type === recordType && normalized.from_record_id === recordId)
    || (normalized.to_record_type === recordType && normalized.to_record_id === recordId)
  );
};

const serverFetch = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const accountDb = db;
  const headers = new Headers(init?.headers as HeadersInit | undefined);
  const token = getAuthToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const response = await fetch(`${getEnv('VITE_API_URL') || ''}${path}`, { ...init, headers });
  if (accountDb !== db) throw new Error('Workspace changed; response ignored.');
  if (!response.ok) {
    throw new Error(`Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
};


const PROJECT_ACTIVE_STATUSES: ProjectStatus[] = ['todo', 'in_process', 'paused'];
const PROJECT_TERMINAL_STATUSES: ProjectStatus[] = ['completed', 'archived'];
const PROJECT_STATUS_SET = new Set<ProjectStatus>([
  ...PROJECT_ACTIVE_STATUSES,
  ...PROJECT_TERMINAL_STATUSES,
]);

const normalizeProjectStatus = (status: Project['status'] | string | null | undefined): ProjectStatus =>
  PROJECT_STATUS_SET.has(status as ProjectStatus) ? (status as ProjectStatus) : 'todo';

const applyWorkLogDuration = (workLog: WorkLog): WorkLog => ({
  ...workLog,
  duration_ms: deriveWorkLogDurationMs(workLog),
});

const stopActiveWorkLogsForUser = async (userId: string, stopAtIso: string): Promise<WorkLog[]> => {
  const repo = getRepo<WorkLog>('workLogs');
  const workLogs = await repo.list();
  const activeLogs = workLogs.filter((workLog) => workLog.user_id === userId && workLog.state === 'active');

  for (const workLog of activeLogs) {
    await repo.upsert({
      ...workLog,
      state: 'stopped',
      end_time: workLog.end_time || stopAtIso,
      updated_at: stopAtIso,
    });
  }

  return activeLogs;
};

const stopActiveWorkLogsForProject = async (projectId: string, stopAtIso: string): Promise<WorkLog[]> => {
  const repo = getRepo<WorkLog>('workLogs');
  const workLogs = await repo.list();
  const activeLogs = workLogs.filter((workLog) => workLog.project_id === projectId && (workLog.state === 'active' || workLog.state === 'paused'));

  for (const workLog of activeLogs) {
    await repo.upsert({
      ...workLog,
      state: 'stopped',
      end_time: workLog.end_time || stopAtIso,
      updated_at: stopAtIso,
    });
  }

  return activeLogs;
};

type Repository<T extends BaseEntity> = {
  list(params?: any): Promise<T[]>;
  get(id: string): Promise<T | undefined>;
  upsert(item: T): Promise<T>;
  remove(id: string): Promise<void>;
  bulkUpsert?(items: T[]): Promise<void>;
};

const repositoryCache: Partial<Record<EntityType, Repository<any>>> = {};

const getRepo = <T extends BaseEntity>(entity: EntityType): Repository<T> => {
  return getRepositoryForEntity<T>(entity) as Repository<T>;
};

const getServerRepo = <T extends BaseEntity>(entity: EntityType): ServerRepository<T> => {
  const repo = getRepositoryForEntity<T>(entity);
  if (!(repo instanceof ServerRepository) || getRepositoryKind(entity) !== 'server') {
    throw new Error(`Server repository required for ${entity}. Check authentication and API configuration.`);
  }
  return repo as ServerRepository<T>;
};

const bulkUpsertOrSequential = async <T extends BaseEntity>(entity: EntityType, items: T[]) => {
  const repo = getRepo<T>(entity);
  if (typeof repo.bulkUpsert === 'function') {
    await repo.bulkUpsert(items);
    return;
  }
  for (const item of items) {
    await repo.upsert(item);
  }
};

const ensureDefaultScoutingCategory = async (): Promise<ScoutingPhotoCategory> => {
    const repo = getRepo<ScoutingPhotoCategory>('scoutingPhotoCategories');
    const existing = await repo.get(DEFAULT_SCOUTING_CATEGORY_ID);
    if (existing) return existing;

    const all = await repo.list();
    if (all.length > 0) return all[0];

    const now = new Date().toISOString();
    const fallback: ScoutingPhotoCategory = {
        category_id: DEFAULT_SCOUTING_CATEGORY_ID,
        name: 'Field Captures',
        color: '#0ea5e9',
        description: 'Default category for scouting gallery photos.',
        server_version: 0,
        created_at: now,
        updated_at: now
    };

    return repo.upsert(fallback);
};

let scoutingBootstrapComplete = false;
let scoutingBootstrapPromise: Promise<void> | null = null;

const markMigrationComplete = () => {
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(SCOUTING_MIGRATION_KEY, new Date().toISOString());
        }
    } catch (e) {
        console.warn('[scouting] Failed to persist migration flag', e);
    }
};

const hasCompletedMigration = () => {
    try {
        if (typeof localStorage === 'undefined') return false;
        return !!localStorage.getItem(SCOUTING_MIGRATION_KEY);
    } catch {
        return false;
    }
};

const migrateLegacyGalleryDocuments = async () => {
    if (hasCompletedMigration()) return;

    const docRepo = getRepo<Document>('documents');
    const photoRepo = getRepo<ScoutingPhoto>('scoutingPhotos');
    const linkRepo = getRepo<RecordLink>('links');
    const defaultCategory = await ensureDefaultScoutingCategory();
    const docs = await docRepo.list();
    const now = new Date().toISOString();
    let migrated = 0;

    for (const doc of docs) {
        const ext = (doc.file_type || '').toLowerCase();
        if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) continue;

        const photoId = doc.document_id || generateId();
        const exists = await photoRepo.get(photoId);
        if (exists) continue;

        const storageUrl = doc.file_base64 || doc.file_path;
        if (!storageUrl) continue;

        const photo: ScoutingPhoto = {
            photo_id: photoId,
            tag_category_ids: [defaultCategory.category_id],
            source: 'Upload',
            created_by: doc.source || 'unknown',
            storage_url: storageUrl,
            uploaded_at: doc.uploaded_date || now,
            taken_at: doc.created_date,
            notes: doc.notes || doc.title,
            server_version: doc.server_version || 0,
            created_at: doc.created_at || now,
            updated_at: doc.updated_at || now
        };

        await photoRepo.upsert(photo);
        const relatedLinks = await linkRepo.list({ where: { document_id: doc.document_id } });
        for (const link of relatedLinks) {
            if (link.photo_id === photoId) continue;
            await linkRepo.upsert({ ...link, photo_id: photoId, updated_at: now });
        }
        migrated++;
    }

    if (migrated > 0) {
        console.log(`[scouting] migrated ${migrated} legacy gallery documents to scouting photos`);
    }
    markMigrationComplete();
};

const ensureScoutingBootstrap = async () => {
    if (scoutingBootstrapComplete) return;
    if (!scoutingBootstrapPromise) {
        scoutingBootstrapPromise = (async () => {
            try {
                await ensureDefaultScoutingCategory();
                await migrateLegacyGalleryDocuments();
                scoutingBootstrapComplete = true;
            } catch (err) {
                console.error('[scouting] bootstrap failed', err);
            }
        })().finally(() => {
            scoutingBootstrapPromise = null;
        });
    }
    return scoutingBootstrapPromise;
};

export const seedDefaultFarm = async () => {
    if (!shouldSeedDefaultFarm()) return;

    const farms = await getRepo<Farm>('farms').list();
    if (farms.length > 0) return;

    const now = new Date().toISOString();
    const farm: Farm = {
        farm_id: 'demo-farm',
        name: 'Prairie View Demo Farm',
        address: '13515 N 1750th Ave, Sheffield, IL 61361',
        county: 'Bureau',
        state: 'IL',
        boundary_geojson: DEFAULT_FARM_BOUNDARY,
        notes: 'Starter farm to explore Sonata Fields mapping tools. You can edit or replace it anytime.',
        server_version: 0,
        created_at: now,
        updated_at: now
    };

    await bulkUpsertOrSequential<Farm>('farms', [farm]);
};

// --- API Facade ---
export class MockAPI {
  static sync() { return SyncService.sync(); }
  static seedDefaultFarm() { return seedDefaultFarm(); }

  private static retiredMethod(method: string): never {
      throw new Error(`${method} has been retired and is no longer supported.`);
  }

  static async resetDatabase() { 
      // Dexie delete returns a promise
      await (db as any).delete();
      window.location.reload();
  }

  static async exportDatabase() {
      // Database export for active collections only.
      return {
          farms: await getRepo<Farm>('farms').list(),
          tasks: await getRepo<OMTask>('tasks').list(),
          documents: await getRepo<Document>('documents').list(),
          messages: await getRepo<CorrespondenceMessage>('messages').list(),
          links: await getRepo<RecordLink>('links').list(),
          mapFeatures: await getRepo<MapFeature>('mapFeatures').list(),
          assets: await getRepo<Asset>('assets').list(),
          scoutingPhotoCategories: await getRepo<ScoutingPhotoCategory>('scoutingPhotoCategories').list(),
          scoutingPhotos: await getRepo<ScoutingPhoto>('scoutingPhotos').list(),
          exportedAt: new Date().toISOString(),
          version: "3.0"
      };
  }

  static async importDatabase(data: any) {
      // Backward-compatible import: ignore retired legacy collections when present.
      const {
          contacts: _contacts,
          tracts: _tracts,
          fields: _fields,
          contracts: _contracts,
          contractPractices: _contractPractices,
          contractFields: _contractFields,
          transactions: _transactions,
          categories: _categories,
          practices: _practices,
          ...activeData
      } = data || {};

      if (activeData.farms) await bulkUpsertOrSequential<Farm>('farms', activeData.farms);
      if (activeData.tasks) await bulkUpsertOrSequential<OMTask>('tasks', activeData.tasks);
      if (activeData.documents) await bulkUpsertOrSequential<Document>('documents', activeData.documents);
      if (activeData.messages) await bulkUpsertOrSequential<CorrespondenceMessage>('messages', activeData.messages);
      if (activeData.links) await bulkUpsertOrSequential<RecordLink>('links', activeData.links);
      if (activeData.mapFeatures) await bulkUpsertOrSequential<MapFeature>('mapFeatures', activeData.mapFeatures);
      if (activeData.assets) await bulkUpsertOrSequential<Asset>('assets', activeData.assets);
      if (activeData.scoutingPhotoCategories) await bulkUpsertOrSequential<ScoutingPhotoCategory>('scoutingPhotoCategories', activeData.scoutingPhotoCategories);
      if (activeData.scoutingPhotos) await bulkUpsertOrSequential<ScoutingPhoto>('scoutingPhotos', activeData.scoutingPhotos);
  }


  // --- Entity Services ---
  static async getContacts() { return this.retiredMethod('MockAPI.getContacts'); }
  static async getContact(_id: string) { return this.retiredMethod('MockAPI.getContact'); }
  static async saveContact(_contact: Contact) { return this.retiredMethod('MockAPI.saveContact'); }
  static async deleteContact(_id: string) { return this.retiredMethod('MockAPI.deleteContact'); }

  static async getFarms() { return getRepo<Farm>('farms').list(); }
  static async getFarm(id: string) { return getRepo<Farm>('farms').get(id); }
  static async getSingletonFarm() {
      const all = await getRepo<Farm>('farms').list();
      return all.length > 0 ? all[0] : undefined;
  }
  static async saveFarm(farm: Farm) { return getRepo<Farm>('farms').upsert(farm); }
  static async deleteFarm(id: string) { 
    await getRepo<Farm>('farms').remove(id); 
  }

  static async getTracts(_farmId: string) { return this.retiredMethod('MockAPI.getTracts'); }
  static async getTract(_id: string) { return this.retiredMethod('MockAPI.getTract'); }
  static async saveTract(_tract: FarmTract) { return this.retiredMethod('MockAPI.saveTract'); }
  static async deleteTract(_id: string) { return this.retiredMethod('MockAPI.deleteTract'); }

  static async getFields(_tractId: string) { return this.retiredMethod('MockAPI.getFields'); }
  static async getAllFields() { return this.retiredMethod('MockAPI.getAllFields'); }
  static async getField(_id: string) { return this.retiredMethod('MockAPI.getField'); }
  static async saveField(_field: FarmField) { return this.retiredMethod('MockAPI.saveField'); }
  static async deleteField(_id: string) { return this.retiredMethod('MockAPI.deleteField'); }

  static async getContracts() { return this.retiredMethod('MockAPI.getContracts'); }
  static async getContract(_id: string) { return this.retiredMethod('MockAPI.getContract'); }
  static async saveContract(_contract: CRPContract) { return this.retiredMethod('MockAPI.saveContract'); }
  static async deleteContract(_id: string) { return this.retiredMethod('MockAPI.deleteContract'); }

  static async getContractPractices(_contractId: string) { return this.retiredMethod('MockAPI.getContractPractices'); }
  static async getAllContractPractices() { return this.retiredMethod('MockAPI.getAllContractPractices'); }
  static async getContractPractice(_id: string) { return this.retiredMethod('MockAPI.getContractPractice'); }
  static async saveContractPractice(_practice: ContractPractice) { return this.retiredMethod('MockAPI.saveContractPractice'); }
  static async deleteContractPractice(_id: string) { return this.retiredMethod('MockAPI.deleteContractPractice'); }

  static async getContractFields(_contractPracticeId: string) { return this.retiredMethod('MockAPI.getContractFields'); }
  static async getAllContractFields() { return this.retiredMethod('MockAPI.getAllContractFields'); }
  static async getContractField(_id: string) { return this.retiredMethod('MockAPI.getContractField'); }
  static async getContractFieldsByFieldId(_fieldId: string) { return this.retiredMethod('MockAPI.getContractFieldsByFieldId'); }
  static async saveContractField(_cf: ContractField) { return this.retiredMethod('MockAPI.saveContractField'); }
  static async deleteContractField(_id: string) { return this.retiredMethod('MockAPI.deleteContractField'); }

  static async getAllContractFieldsForContract(_contractId: string) { return this.retiredMethod('MockAPI.getAllContractFieldsForContract'); }

  static async getPractices() { return this.retiredMethod('MockAPI.getPractices'); }
  
  static async getTasks(contractFieldId?: string) {
    if (contractFieldId) {
        const all = await getRepo<OMTask>('tasks').list();
        return all.filter(t => t.contract_field_id === contractFieldId || (t.related_entity_type === 'contract_field' && t.related_entity_id === contractFieldId));
    }
    return getRepo<OMTask>('tasks').list();
  }
  static async getTasksForAsset(assetId: string) {
      const all = await getRepo<OMTask>('tasks').list();
      return all.filter(t => t.asset_id === assetId || (t.related_entity_type === 'asset' && t.related_entity_id === assetId));
  }
  static async saveTask(task: OMTask) { return getRepo<OMTask>('tasks').upsert(task); }


  static async getProjects() {
    const projects = await getRepo<Project>('projects').list();
    return projects.map((project) => ({
      ...project,
      status: normalizeProjectStatus(project.status as string | undefined),
    }));
  }

  static async getProject(id: string) {
    const project = await getRepo<Project>('projects').get(id);
    if (!project) return project;
    return {
      ...project,
      status: normalizeProjectStatus(project.status as string | undefined),
    };
  }

  static async saveProject(project: Project) {
    const now = new Date().toISOString();
    const projectRepo = getRepo<Project>('projects');
    const existing = await projectRepo.get(project.id);
    const existingStatus = normalizeProjectStatus(existing?.status as string | undefined);
    const incomingStatus = normalizeProjectStatus(project.status as string | undefined);

    const usingServerRepository = projectRepo instanceof ServerRepository;

    // Server-backed project updates are guarded by DB constraints/triggers.
    // Avoid client-side transition/work-log guards here because they can fail due
    // auth/permission differences and block otherwise valid detail updates.
    if (!usingServerRepository) {
      if (existing && PROJECT_TERMINAL_STATUSES.includes(existingStatus) && existingStatus !== incomingStatus) {
        throw new Error(`Project transition not allowed from ${existingStatus} to ${incomingStatus}.`);
      }

      const movingToTerminalState = PROJECT_TERMINAL_STATUSES.includes(incomingStatus)
        && (!existing || !PROJECT_TERMINAL_STATUSES.includes(existingStatus));

      if (movingToTerminalState) {
        // Guard condition: stop active/paused logs before allowing the status transition.
        // If this fails, the project update is not persisted.
        await stopActiveWorkLogsForProject(project.id, now);
      }
    }

    const saved = await projectRepo.upsert({
      ...project,
      status: incomingStatus,
      updated_at: now,
      created_at: project.created_at || existing?.created_at || now,
    });

    return {
      ...saved,
      status: normalizeProjectStatus(saved.status as string | undefined),
    };
  }

  static async deleteProject(id: string) {
    await getRepo<Project>('projects').remove(id);
  }

  static computeWorkLogDuration(workLog?: WorkLog, nowMs: number = Date.now()) {
    if (!workLog) return 0;
    return deriveWorkLogDurationMs(workLog, new Date(nowMs).toISOString());
  }

  static async getCurrentActiveTimerByUser(_userId: string) {return (await workState()).work.find(w=>w.state==='active');}
  static async getWorkLogs(projectId?:string,userId?:string) {return (await workState()).work.filter(w=>(!projectId||w.project_id===projectId)&&(!userId||w.user_id===userId||w.contributor_email===userId)).map(applyWorkLogDuration);}
  static async saveWorkLog(_workLog:WorkLog):Promise<WorkLog> {throw new Error('Use Log Work or contribution corrections.');}
  static async startTimerForProject(payload:{project_id:string;user_id:string;start_time?:string}) {return timerCommand('start',undefined,payload.project_id);}
  static async pauseTimer(id:string) {return timerCommand('pause',id);}
  static async resumeWorkTimer(id:string) {return timerCommand('resume',id);}
  static async stopTimer(id:string,_endTime?:string) {return timerCommand('stop',id);}
  static async startWorkTimer(payload:{project_id:string;user_id:string;start_time?:string}) {return this.startTimerForProject(payload);}
  static async pauseWorkTimer(id:string) {return this.pauseTimer(id);}
  static async stopWorkTimer(id:string,endTime?:string) {return this.stopTimer(id,endTime);}
  static async deleteWorkLog(_id:string) {throw new Error('Use an audited contribution correction.');}

  static async getTransactions() { return this.retiredMethod('MockAPI.getTransactions'); }
  static async getTransaction(_id: string) { return this.retiredMethod('MockAPI.getTransaction'); }
  static async saveTransaction(_t: Transaction) { return this.retiredMethod('MockAPI.saveTransaction'); }
  static async deleteTransaction(_id: string) { return this.retiredMethod('MockAPI.deleteTransaction'); }

  static async getMapFeatures(farmId: string) { return getRepo<MapFeature>('mapFeatures').list({ where: { farm_id: farmId } }); }
  static async saveMapFeature(feature: MapFeature) { return getRepo<MapFeature>('mapFeatures').upsert(feature); }
  static async deleteMapFeature(feature: MapFeature) {
      if (feature.map_feature_id) await getRepo<MapFeature>('mapFeatures').remove(feature.map_feature_id);
      if (feature.entity_type && feature.entity_id) {
          if (feature.entity_type === 'farm') {
              const rec = await getRepo<Farm>('farms').get(feature.entity_id);
              if (rec) { rec.boundary_geojson = null as any; await getRepo<Farm>('farms').upsert(rec); }
          }
      }
  }

  static async getDocuments() { return getRepo<Document>('documents').list(); }
  static async getDocument(id: string) { return getRepo<Document>('documents').get(id); }
  static async getRecordLinks() { return (await getRepo<RecordLink>('links').list()).map(normalizeRecordLink); }
  static async getRecordLinksForDocument(docId: string) {
    const links = await getRepo<RecordLink>('links').list();
    return links.map(normalizeRecordLink).filter((link) => linkMatchesRecord(link, 'document', docId));
  }
  static async getRecordLinksForPhoto(photoId: string) {
    const repo = getRepo<RecordLink>('links');
    if (repo instanceof ServerRepository) {
        return ScoutingClient.listPhotoLinks(photoId);
    }
    const links = await repo.list();
    return links.map(normalizeRecordLink).filter((link) => linkMatchesRecord(link, 'scouting_photo', photoId));
  }
  static async saveDocument(doc: Document) { return getRepo<Document>('documents').upsert(doc); }
  static async saveRecordLink(link: RecordLink) {
    const repo = getRepo<RecordLink>('links');
    const normalized = normalizeRecordLink(link);
    if (repo instanceof ServerRepository && normalized.photo_id) {
        return ScoutingClient.upsertPhotoLink(normalized.photo_id, { record_link_id: normalized.record_link_id, entity_id: normalized.from_record_type === 'scouting_photo' ? normalized.to_record_id : normalized.from_record_id, entity_type: normalized.from_record_type === 'scouting_photo' ? normalized.to_record_type : normalized.from_record_type });
    }
    return repo.upsert(normalized);
  }
  static async deleteRecordLink(id: string) {
    const repo = getRepo<RecordLink>('links');
    if (repo instanceof ServerRepository) {
        // When server-backed, require caller to include photo_id. Fallback to direct delete if not provided.
        const existing = await repo.get(id);
        if (existing?.photo_id) {
            await ScoutingClient.deletePhotoLink(existing.photo_id, id);
            return;
        }
    }
    await repo.remove(id);
  }

  static async linkDocumentToProject(documentId: string, projectId: string) {
    const projectsRepo = getRepo<Project>('projects');
    const linksRepo = getRepo<RecordLink>('links');

    if (projectsRepo instanceof ServerRepository && linksRepo instanceof ServerRepository) {
      return serverFetch<RecordLink>('/api/document-project-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_id: documentId, project_id: projectId }),
      });
    }

    const project = await projectsRepo.get(projectId);
    if (!project) throw new Error('Project not found.');
    if (!PROJECT_ACTIVE_STATUSES.includes(project.status)) throw new Error('Project must be todo, in_process, or paused.');

    const allLinks = (await linksRepo.list()).map(normalizeRecordLink);
    const existing = allLinks.find((link) => linkMatchesRecord(link, 'document', documentId) && linkMatchesRecord(link, 'project', projectId));
    const now = new Date().toISOString();

    return linksRepo.upsert({
      ...(existing || {} as RecordLink),
      record_link_id: existing?.record_link_id || generateId(),
      from_record_type: 'document',
      from_record_id: documentId,
      to_record_type: 'project',
      to_record_id: projectId,
      entity_type: 'project',
      entity_id: projectId,
      document_id: documentId,
      linked_date: existing?.linked_date || now,
      linked_by: existing?.linked_by || 'User',
      server_version: Date.now(),
      deleted_at: undefined,
      updated_at: now,
      created_at: existing?.created_at || now,
    });
  }

  static async unlinkDocumentFromProject(recordLinkId: string) {
    const linksRepo = getRepo<RecordLink>('links');
    if (linksRepo instanceof ServerRepository) {
      await serverFetch<RecordLink>(`/api/document-project-links/${recordLinkId}`, { method: 'DELETE' });
      return;
    }
    await linksRepo.remove(recordLinkId);
  }

  static async getLinkableProjects() {
    const projects = await getRepo<Project>('projects').list();
    return projects.filter((project) => PROJECT_ACTIVE_STATUSES.includes(project.status));
  }
  static async deleteDocument(id: string) {
    const linksRepo = getServerRepo<RecordLink>('links');
    const documentRepo = getServerRepo<Document>('documents');

    const links = (await linksRepo.list()).map(normalizeRecordLink).filter((link) => linkMatchesRecord(link, 'document', id));
    for (const l of links) await linksRepo.remove(l.record_link_id);
    await documentRepo.remove(id);
  }
  static async getScoutingPhotoCategories() {
    await ensureScoutingBootstrap();
    const repo = getRepo<ScoutingPhotoCategory>('scoutingPhotoCategories');
    if (repo instanceof ServerRepository) {
        return ScoutingClient.listCategories();
    }
    return repo.list();
  }
  static async createScoutingPhotoCategory(payload: { name: string; color?: string; description?: string }) {
    await ensureScoutingBootstrap();
    const repo = getRepo<ScoutingPhotoCategory>('scoutingPhotoCategories');
    const now = new Date().toISOString();
    const category: ScoutingPhotoCategory = {
        category_id: generateId(),
        name: payload.name.trim(),
        color: payload.color?.trim() || undefined,
        description: payload.description?.trim() || undefined,
        server_version: Date.now(),
        created_at: now,
        updated_at: now,
    };

    if (repo instanceof ServerRepository) {
        return ScoutingClient.createCategory(category);
    }
    return repo.upsert(category);
  }
  static async updateScoutingPhotoCategory(categoryId: string, payload: { name?: string; color?: string; description?: string }) {
    await ensureScoutingBootstrap();
    const repo = getRepo<ScoutingPhotoCategory>('scoutingPhotoCategories');
    const existing = await repo.get(categoryId);
    if (!existing) throw new Error('Category not found');

    const now = new Date().toISOString();
    const normalizedColor =
        payload.color === undefined ? existing.color : payload.color.trim() || undefined;

    const updated: ScoutingPhotoCategory = {
        ...existing,
        name: payload.name?.trim() || existing.name,
        color: normalizedColor,
        description: payload.description?.trim() ?? existing.description,
        updated_at: now,
        server_version: Date.now(),
    };

    if (repo instanceof ServerRepository) {
        return ScoutingClient.updateCategory(categoryId, updated);
    }
    return repo.upsert(updated);
  }
  static async deleteScoutingPhotoCategory(categoryId: string) {
    await ensureScoutingBootstrap();
    const repo = getRepo<ScoutingPhotoCategory>('scoutingPhotoCategories');
    if (repo instanceof ServerRepository) {
        await ScoutingClient.deleteCategory(categoryId);
        return;
    }
    await repo.remove(categoryId);
  }
  static async saveScoutingPhotoCategory(category: ScoutingPhotoCategory) {
    await ensureScoutingBootstrap();
    const repo = getRepo<ScoutingPhotoCategory>('scoutingPhotoCategories');
    const payload = {
        ...category,
        name: category.name.trim(),
        color: category.color?.trim() || undefined,
        description: category.description?.trim() || undefined,
        updated_at: category.updated_at || new Date().toISOString(),
        created_at: category.created_at || new Date().toISOString(),
        server_version: category.server_version ?? Date.now(),
    };

    if (repo instanceof ServerRepository) {
        return ScoutingClient.updateCategory(category.category_id, payload);
    }
    return repo.upsert(payload);
  }
  static async getScoutingPhotos() {
    await ensureScoutingBootstrap();
    const repo = getRepo<ScoutingPhoto>('scoutingPhotos');
    if (repo instanceof ServerRepository) {
        return ScoutingClient.listPhotos();
    }
    return repo.list();
  }
  static async getScoutingPhotosWithFilters(filters: ScoutingPhotoFilters) {
    await ensureScoutingBootstrap();
    const repo = getRepo<ScoutingPhoto>('scoutingPhotos');
    if (repo instanceof ServerRepository) {
        return ScoutingClient.listPhotos(filters);
    }
    const photos = await repo.list();
    const effectiveFilterCategoryIds = getEffectiveFilterCategoryIds(filters);
    return photos.filter((p) => {
        if (effectiveFilterCategoryIds.length > 0) {
            const effectivePhotoCategoryIds = getEffectivePhotoCategoryIds(p as ScoutingPhoto & { category_id?: string | null });
            if (!effectiveFilterCategoryIds.some((categoryId) => effectivePhotoCategoryIds.includes(categoryId))) return false;
        }
        if (filters.source && p.source !== filters.source) return false;
        if (filters.startDate && new Date(p.uploaded_at) < new Date(filters.startDate)) return false;
        if (filters.endDate && new Date(p.uploaded_at) > new Date(filters.endDate)) return false;
        return true;
    });
  }
  static async uploadScoutingPhoto(upload: ScoutingPhotoUploadRequest) {
    await ensureScoutingBootstrap();
    const repo = getRepo<ScoutingPhoto>('scoutingPhotos');
    const now = new Date().toISOString();
    const photoId = upload.photo_id || generateId();
    const uploadedAt = upload.uploaded_at || now;
    const takenAt = upload.taken_at || upload.exif?.taken_at;

    if (repo instanceof ServerRepository) {
        return ScoutingClient.uploadPhoto({
            ...upload,
            photo_id: photoId,
            uploaded_at: uploadedAt,
            taken_at: takenAt,
        });
    }

    const storageUrl = upload.file_base64.startsWith('data:')
        ? upload.file_base64
        : `data:image/jpeg;base64,${upload.file_base64}`;

    const photo: ScoutingPhoto = {
        photo_id: photoId,
        source: upload.source,
        created_by: upload.created_by || 'unknown',
        storage_url: storageUrl,
        uploaded_at: uploadedAt,
        taken_at: takenAt || uploadedAt,
        exif_lat: upload.exif?.location?.lat,
        exif_lng: upload.exif?.location?.lng,
        exif_alt: upload.exif?.location?.alt,
        notes: upload.notes,
        tag_category_ids: upload.tag_category_ids,
        server_version: Date.now(),
        created_at: now,
        updated_at: now,
    };
    return repo.upsert(photo);
  }
  static async saveScoutingPhoto(photo: ScoutingPhoto) {
    await ensureScoutingBootstrap();
    const repo = getRepo<ScoutingPhoto>('scoutingPhotos');
    if (repo instanceof ServerRepository) {
        return ScoutingClient.upsertPhoto(photo);
    }
    return repo.upsert(photo);
  }
  static async deleteScoutingPhoto(id: string) {
    const links = (await getRepo<RecordLink>('links').list()).map(normalizeRecordLink).filter((link) => linkMatchesRecord(link, 'scouting_photo', id));
    for (const l of links) await getRepo<RecordLink>('links').remove(l.record_link_id);
    const repo = getRepo<ScoutingPhoto>('scoutingPhotos');
    if (repo instanceof ServerRepository) {
        await ScoutingClient.deletePhoto(id);
        return;
    }
    await repo.remove(id);
  }
  static async getScoutingPhotosForEntity(entityType: string, entityId: string) {
    await ensureScoutingBootstrap();
    const repo = getRepo<ScoutingPhoto>('scoutingPhotos');
    if (repo instanceof ServerRepository) {
        const filters: ScoutingPhotoFilters = {};
        if (entityType === 'farm') filters.farmId = entityId;
        if (entityType === 'farm_tract') filters.tractId = entityId;
        if (entityType === 'farm_field') filters.fieldId = entityId;
        if (entityType === 'asset') filters.assetId = entityId;
        return ScoutingClient.listPhotos(filters);
    }
    const links = (await getRepo<RecordLink>('links').list()).map(normalizeRecordLink).filter((l) => linkMatchesRecord(l, entityType, entityId));
    const photoIds = links.map((l) => normalizeRecordLink(l)).filter((l) => l.from_record_type === 'scouting_photo' || l.to_record_type === 'scouting_photo').map((l) => l.from_record_type === 'scouting_photo' ? l.from_record_id : l.to_record_id);
    if (photoIds.length === 0) return [];
    const photos = await Promise.all(photoIds.map(pid => getRepo<ScoutingPhoto>('scoutingPhotos').get(pid)));
    return photos.filter((p): p is ScoutingPhoto => !!p);
  }
  static async getDocumentsForEntity(entityType: string, entityId: string, includePhotos = true) {
    await ensureScoutingBootstrap();
    const links = (await getRepo<RecordLink>('links').list()).map(normalizeRecordLink).filter((l) => linkMatchesRecord(l, entityType, entityId));
    const docIds = links.map((l) => normalizeRecordLink(l)).filter((l) => l.from_record_type === 'document' || l.to_record_type === 'document').map((l) => l.from_record_type === 'document' ? l.from_record_id : l.to_record_id);
    const photoIds = links.map((l) => normalizeRecordLink(l)).filter((l) => l.from_record_type === 'scouting_photo' || l.to_record_type === 'scouting_photo').map((l) => l.from_record_type === 'scouting_photo' ? l.from_record_id : l.to_record_id);
    const docs = docIds.length > 0 ? await Promise.all(docIds.map(did => getRepo<Document>('documents').get(did))) : [];
    const photos = includePhotos && photoIds.length > 0 ? await Promise.all(photoIds.map(pid => getRepo<ScoutingPhoto>('scoutingPhotos').get(pid))) : [];

    const validDocs = docs.filter((d): d is Document => !!d);
    const mappedPhotos: Document[] = photos
        .filter((p): p is ScoutingPhoto => !!p)
        .map((photo) => ({
            document_id: photo.photo_id,
            title: photo.notes || 'Scouting Photo',
            description: photo.notes,
            file_path: photo.storage_url,
            file_base64: photo.storage_url,
            file_type: 'jpg',
            source: DocumentSource.Uploaded,
            uploaded_date: photo.uploaded_at,
            is_confidential: false,
            notes: photo.notes,
            server_version: photo.server_version || 0,
            created_at: photo.created_at,
            updated_at: photo.updated_at
        }));

    const combined = [...validDocs, ...mappedPhotos];
    combined.sort((a, b) => new Date(b.uploaded_date).getTime() - new Date(a.uploaded_date).getTime());
    return combined;
  }

  static async getMessagesForEntity(entityType: string, entityId: string) {
      const links = (await getRepo<RecordLink>('links').list()).map(normalizeRecordLink).filter((l) => linkMatchesRecord(l, entityType, entityId));
      const messageIds = links.filter(l => !!l.message_id).map(l => l.message_id!);
      if (messageIds.length === 0) return [];
      const messages = await Promise.all(messageIds.map(mid => getRepo<CorrespondenceMessage>('messages').get(mid)));
      const validMsgs = messages.filter((m): m is CorrespondenceMessage => !!m);
      return validMsgs.sort((a, b) => new Date(b.message_datetime).getTime() - new Date(a.message_datetime).getTime());
  }

  static async getMessagesForEntities(targets: { entityType: string; entityId: string }[]) {
      const allLinks = await getRepo<RecordLink>('links').list();
      const relevantLinks = allLinks.map(normalizeRecordLink).filter((l) => (l.from_record_type === 'message' || l.to_record_type === 'message') && targets.some(t => linkMatchesRecord(l, t.entityType, t.entityId)));
      const messageIds = [...new Set(relevantLinks.map((l) => l.from_record_type === 'message' ? l.from_record_id : l.to_record_id))];
      if (messageIds.length === 0) return [];
      const messages = await Promise.all(messageIds.map(mid => getRepo<CorrespondenceMessage>('messages').get(mid)));
      const validMsgs = messages.filter((m): m is CorrespondenceMessage => !!m);
      return validMsgs.sort((a, b) => new Date(b.message_datetime).getTime() - new Date(a.message_datetime).getTime());
  }

  static async saveNoteForEntity(message: CorrespondenceMessage, entityType: string, entityId: string) {
      const savedMsg = await getRepo<CorrespondenceMessage>('messages').upsert(message);
      const link: RecordLink = {
          record_link_id: '',
          from_record_type: 'message',
          from_record_id: savedMsg.message_id,
          to_record_type: entityType as any,
          to_record_id: entityId,
          entity_type: entityType as any,
          entity_id: entityId,
          message_id: savedMsg.message_id,
          linked_date: new Date().toISOString(),
          linked_by: 'User',
          server_version: 0
      };
      await getRepo<RecordLink>('links').upsert(link);
      return savedMsg;
  }

  static async deleteMessage(id: string) {
      const links = (await getRepo<RecordLink>('links').list()).map(normalizeRecordLink).filter((l) => linkMatchesRecord(l, 'message', id));
      for (const l of links) await getRepo<RecordLink>('links').remove(l.record_link_id);
      await getRepo<CorrespondenceMessage>('messages').remove(id);
  }

  static async getAssets() { return getRepo<Asset>('assets').list(); }
  static async getAsset(id: string) { return getRepo<Asset>('assets').get(id); }
  static async saveAsset(asset: Asset) { return getRepo<Asset>('assets').upsert(asset); }
  static async deleteAsset(id: string) { await getRepo<Asset>('assets').remove(id); }

  static async getStats() {
    const farms = await getRepo<Farm>('farms').list();
    return {
      totalFarms: farms.length,
      activeContracts: 0,
      totalAcres: 0,
      expiringThisYear: 0
    };
  }
}
